import React, { useEffect, useState } from "react";
import { Car, Droplets, Plus, X, MapPin, Phone, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLocation as useUserLocation } from "@/context/LocationContext";
import { useSeo } from "@/hooks/useSeo";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// -----------------------------------------------------------------------------
// CarServices — authorised + unauthorised + washing/cleaning/detailing centres.
// Any user can add; the listing sorts by GPS distance when available.
// -----------------------------------------------------------------------------
const KINDS = [
  { k:"authorised",       l:"Authorised" },
  { k:"unauthorised",     l:"Unauthorised" },
  { k:"washing",          l:"Washing" },
  { k:"cleaning",         l:"Cleaning" },
  { k:"detailing",        l:"Detailing" },
  { k:"tyre",             l:"Tyre" },
  { k:"denting_painting", l:"Denting & Painting" },
];

export default function CarServices() {
  const { user } = useAuth();
  const { loc, detect } = useUserLocation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState("");
  const [q, setQ] = useState("");
  const [showForm, setShowForm] = useState(false);

  useSeo({ title:"Car service centres near you · find2near",
           description:"Authorised & independent car service, washing, cleaning, detailing centres — prices, distance, contact.",
           canonical:"/car-services" });

  const load = () => {
    setLoading(true);
    const p = { limit:60 };
    if (kind) p.kind = kind;
    if (q) p.q = q;
    if (loc.lat && loc.lng) { p.lat=loc.lat; p.lng=loc.lng; p.radius_km=25; }
    api.get("/car-services",{params:p}).then(r=>setItems(r.data)).catch(()=>setItems([])).finally(()=>setLoading(false));
  };
  useEffect(load, [kind, loc.lat, loc.lng]);   // eslint-disable-line

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-6" data-testid="car-services-page">
      {showForm && <CarForm onClose={()=>setShowForm(false)} onSaved={()=>{setShowForm(false); load();}} />}

      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-stone-500 font-bold flex items-center gap-2"><Car size={12} className="text-slate-700" /> Car services near you</div>
          <h1 className="heading text-3xl md:text-4xl font-black text-stone-900">Every workshop. <span className="text-slate-700">One garage.</span></h1>
          <p className="text-stone-500 text-sm mt-1">Authorised · Local · Washing · Detailing · Tyre · Denting & Painting.</p>
        </div>
        {user && <Button onClick={()=>setShowForm(true)} className="rounded-full bg-slate-700 hover:bg-slate-800 font-black" data-testid="car-new-btn"><Plus size={14} className="mr-1.5"/> Add centre</Button>}
      </div>

      <div className="bg-white border border-stone-200 rounded-3xl p-3 flex flex-wrap gap-2 items-center mb-4">
        <Input value={q} onChange={e=>setQ(e.target.value)} onKeyDown={e=>e.key==="Enter"&&load()} placeholder="Search brand / area…" className="rounded-full h-9 flex-1 min-w-[180px] text-sm" data-testid="car-q" />
        <Select value={kind||"__all__"} onValueChange={v=>setKind(v==="__all__"?"":v)}>
          <SelectTrigger className="rounded-full h-9 w-44 text-xs" data-testid="car-kind"><SelectValue placeholder="All types"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All types</SelectItem>
            {KINDS.map(k=><SelectItem key={k.k} value={k.k}>{k.l}</SelectItem>)}
          </SelectContent>
        </Select>
        <button onClick={()=>detect()} className="text-xs font-bold text-emerald-700 hover:bg-emerald-50 rounded-full px-3 h-9" data-testid="car-locate"><MapPin size={12} className="inline mr-1"/> My location</button>
      </div>

      {loading ? <div className="text-center py-16 text-stone-400"><Loader2 className="mx-auto animate-spin"/></div>
      : items.length===0 ? <div className="text-center py-16 text-stone-400 border border-dashed border-stone-200 rounded-3xl">No centres yet — {user ? "add the first one!" : "sign in to add."}</div>
      : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {items.map(s => (
            <div key={s.service_id} className="rounded-3xl bg-white border border-stone-200 p-4" data-testid={`car-card-${s.service_id}`}>
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className={`text-[10px] font-black uppercase tracking-widest rounded-full px-2 py-0.5 ${s.kind==="authorised" ? "bg-emerald-100 text-emerald-800" : "bg-stone-100 text-stone-800"}`}>{s.kind.replace(/_/g," ")}</span>
                {s.kind==="authorised" && <span className="text-[10px] font-black text-emerald-700 inline-flex items-center gap-0.5"><ShieldCheck size={10}/> OEM</span>}
                {s.brand && s.brand !== "any" && <span className="text-[10px] font-mono text-stone-500 uppercase">{s.brand}</span>}
                {s.distance_km != null && <span className="text-[10px] text-stone-500">· {s.distance_km} km</span>}
              </div>
              <div className="font-black text-stone-900">{s.name}</div>
              {s.services?.length > 0 && <div className="mt-1 text-xs text-stone-600">{s.services.slice(0,5).join(" · ")}</div>}
              {s.price_note && <div className="mt-1 text-xs text-amber-700 font-bold">{s.price_note}</div>}
              {s.address && <div className="mt-1 text-xs text-stone-500 line-clamp-2">{s.address}</div>}
              {s.phone && <a href={`tel:${s.phone}`} className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-emerald-700"><Phone size={11}/> {s.phone}</a>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CarForm({ onClose, onSaved }) {
  const [f, setF] = useState({ name:"", kind:"authorised", brand:"any", services:"", address:"", city:"", lat:"", lng:"", phone:"", price_note:"", open_24x7:false });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (f.name.trim().length < 2) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      await api.post("/car-services", {
        ...f, lat: f.lat==="" ? null : parseFloat(f.lat), lng: f.lng==="" ? null : parseFloat(f.lng),
        services: f.services.split(/[,\n]+/).map(x=>x.trim()).filter(Boolean),
      });
      toast.success("Centre added"); onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail||"Failed"); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl">
        <div className="flex items-center justify-between px-5 py-3 border-b border-stone-200"><b>Add car service centre</b><button onClick={onClose}><X size={18} className="text-stone-500"/></button></div>
        <div className="p-5 space-y-2 max-h-[75vh] overflow-y-auto">
          <Input value={f.name} onChange={e=>setF({...f,name:e.target.value})} placeholder="Centre name" className="rounded-2xl h-10" />
          <div className="grid grid-cols-2 gap-2">
            <Select value={f.kind} onValueChange={v=>setF({...f,kind:v})}>
              <SelectTrigger className="rounded-2xl h-10"><SelectValue/></SelectTrigger>
              <SelectContent>{KINDS.map(k=><SelectItem key={k.k} value={k.k}>{k.l}</SelectItem>)}</SelectContent>
            </Select>
            <Input value={f.brand} onChange={e=>setF({...f,brand:e.target.value})} placeholder="Brand (Maruti, Honda, Any)" className="rounded-2xl h-10" />
          </div>
          <Input value={f.services} onChange={e=>setF({...f,services:e.target.value})} placeholder="Services (comma separated) — washing, oil, denting" className="rounded-2xl h-10 text-xs" />
          <Input value={f.address} onChange={e=>setF({...f,address:e.target.value})} placeholder="Address" className="rounded-2xl h-10" />
          <div className="grid grid-cols-4 gap-2">
            <Input value={f.city} onChange={e=>setF({...f,city:e.target.value})} placeholder="City" className="rounded-2xl h-10 col-span-2" />
            <Input value={f.lat} onChange={e=>setF({...f,lat:e.target.value})} placeholder="Lat" className="rounded-2xl h-10 font-mono" />
            <Input value={f.lng} onChange={e=>setF({...f,lng:e.target.value})} placeholder="Lng" className="rounded-2xl h-10 font-mono" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input value={f.phone} onChange={e=>setF({...f,phone:e.target.value})} placeholder="Phone" className="rounded-2xl h-10 font-mono" />
            <Input value={f.price_note} onChange={e=>setF({...f,price_note:e.target.value})} placeholder="Price hint (₹4500-6500)" className="rounded-2xl h-10" />
          </div>
          <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={f.open_24x7} onChange={e=>setF({...f,open_24x7:e.target.checked})}/> Open 24×7</label>
          <Button onClick={submit} disabled={busy} className="w-full rounded-full bg-slate-700 hover:bg-slate-800 font-black">{busy && <Loader2 className="animate-spin mr-2" size={14}/>} Add</Button>
        </div>
      </div>
    </div>
  );
}
